import { useRef, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Search, ArrowRight, FileText, Database, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';

gsap.registerPlugin(ScrollTrigger);

interface HeroSectionProps {
  onStartSearch: () => void;
  onSearch: (query: string) => void;
  onSeeDemo: () => void;
}

const popularSearches = ['minimum wage', 'equal pay act', 'labor statistics'] as const;

export function HeroSection({ onStartSearch, onSearch, onSeeDemo }: HeroSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const subheadlineRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const stampRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [heroQuery, setHeroQuery] = useState('');

  const submitHeroSearch = (query: string) => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    setHeroQuery(normalizedQuery);
    onSearch(normalizedQuery);
  };

  // Auto-play entrance animation on load
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => setIsLoaded(true),
      });

      // Stamp entrance
      tl.fromTo(
        stampRef.current,
        { opacity: 0, scale: 0.96, rotation: -8 },
        { opacity: 1, scale: 1, rotation: -4, duration: 0.6, ease: 'power2.out' }
      );

      // Headline words stagger
      const words = headlineRef.current?.querySelectorAll('.word');
      if (words) {
        tl.fromTo(
          words,
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7, stagger: 0.02, ease: 'power2.out' },
          '-=0.3'
        );
      }

      // Subheadline
      tl.fromTo(
        subheadlineRef.current,
        { y: 16, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
        '-=0.4'
      );

      // CTA buttons
      tl.fromTo(
        ctaRef.current?.children || [],
        { y: 16, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: 'power2.out' },
        '-=0.3'
      );

      // Search card
      tl.fromTo(
        cardRef.current,
        { x: '10vw', opacity: 0, scale: 0.98 },
        { x: 0, opacity: 1, scale: 1, duration: 0.8, ease: 'power2.out' },
        '-=0.6'
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Scroll-driven exit animation
  useLayoutEffect(() => {
    if (!isLoaded) return;

    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
          onLeaveBack: () => {
            // Reset all elements to visible when scrolling back
            gsap.set(
              [
                headlineRef.current,
                subheadlineRef.current,
                ctaRef.current,
                cardRef.current,
                stampRef.current,
              ],
              {
                opacity: 1,
                x: 0,
                y: 0,
              }
            );
          },
        },
      });

      // EXIT (70% - 100%)
      scrollTl.fromTo(
        headlineRef.current,
        { x: 0, opacity: 1 },
        { x: '-18vw', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(
        subheadlineRef.current,
        { x: 0, opacity: 1 },
        { x: '-14vw', opacity: 0, ease: 'power2.in' },
        0.72
      );

      scrollTl.fromTo(
        ctaRef.current,
        { x: 0, opacity: 1 },
        { x: '-10vw', opacity: 0, ease: 'power2.in' },
        0.74
      );

      scrollTl.fromTo(
        cardRef.current,
        { x: 0, opacity: 1 },
        { x: '18vw', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(
        stampRef.current,
        { y: 0, opacity: 1 },
        { y: '-10vh', opacity: 0, ease: 'power2.in' },
        0.75
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [isLoaded]);

  const headlineWords = 'Turn claims into receipts.'.split(' ');

  return (
    <section
      ref={sectionRef}
      className="section-pinned z-10 flex items-center justify-center bg-[#F6F5F2]"
    >
      {/* Stamp Graphic */}
      <div
        ref={stampRef}
        className="stamp-animate absolute"
        style={{ left: '62vw', top: '8vh', width: '34vw' }}
      >
        <svg viewBox="0 0 200 80" className="h-auto w-full">
          <rect
            x="5"
            y="5"
            width="190"
            height="70"
            rx="8"
            fill="none"
            stroke="#E1062C"
            strokeWidth="3"
            strokeDasharray="8 4"
          />
          <text
            x="100"
            y="48"
            textAnchor="middle"
            fill="#E1062C"
            fontSize="32"
            fontWeight="700"
            fontFamily="Space Grotesk, sans-serif"
          >
            RECEIPT
          </text>
        </svg>
      </div>

      {/* Headline Group */}
      <div
        ref={headlineRef}
        className="absolute"
        style={{ left: '9vw', top: '18vh', width: '44vw' }}
      >
        <h1
          className="font-display font-bold leading-[0.95] tracking-[-0.02em] text-[#0B0D10]"
          style={{ fontSize: 'clamp(44px, 5vw, 72px)' }}
        >
          {headlineWords.map((word, i) => (
            <span key={i} className="word mr-[0.3em] inline-block">
              {word}
            </span>
          ))}
        </h1>
      </div>

      {/* Subheadline */}
      <p
        ref={subheadlineRef}
        className="absolute leading-relaxed text-[#6B6F78]"
        style={{ left: '9vw', top: '44vh', width: '38vw', fontSize: 'clamp(16px, 1.4vw, 20px)' }}
      >
        Search once. Get dated quotes from primary sources—laws, hearings, agency releases—then
        share a clean citation card in seconds.
      </p>

      {/* CTA Row */}
      <div
        ref={ctaRef}
        className="absolute flex items-center gap-4"
        style={{ left: '9vw', top: '62vh' }}
      >
        <Button
          onClick={onStartSearch}
          className="btn-hover flex items-center gap-2 rounded-full bg-[#E1062C] px-6 py-3 font-medium text-white hover:bg-[#c00525]"
        >
          Start a search
          <ArrowRight className="h-4 w-4" />
        </Button>
        <button
          onClick={onSeeDemo}
          className="font-medium text-[#0B0D10] underline underline-offset-4 transition-colors hover:text-[#E1062C]"
        >
          See a demo
        </button>
      </div>

      {/* Search Card */}
      <div
        ref={cardRef}
        className="receipt-card absolute flex flex-col p-6"
        style={{ left: '54vw', top: '30vh', width: '38vw', height: '44vh' }}
      >
        {/* Search Input */}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitHeroSearch(heroQuery);
          }}
          className="flex items-center gap-3 rounded-xl border border-[rgba(11,13,16,0.08)] bg-[#F6F5F2] p-2 pl-4 transition-shadow focus-within:border-[#E1062C]/40 focus-within:ring-4 focus-within:ring-[#E1062C]/10"
        >
          <Search className="h-5 w-5 flex-shrink-0 text-[#6B6F78]" aria-hidden="true" />
          <label htmlFor="hero-search" className="sr-only">
            Search a claim or topic
          </label>
          <input
            id="hero-search"
            type="search"
            value={heroQuery}
            onChange={(event) => setHeroQuery(event.target.value)}
            placeholder="Search a claim or topic…"
            className="min-w-0 flex-1 bg-transparent py-2 text-[#0B0D10] placeholder:text-[#6B6F78] focus:outline-none"
          />
          <button
            type="submit"
            disabled={!heroQuery.trim()}
            className="flex items-center gap-1.5 rounded-lg bg-[#E1062C] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#c00525] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Search
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </form>

        {/* Source Chips */}
        <div className="mt-6">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
            Sources
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="flex items-center gap-2 rounded-lg bg-[#F6F5F2] px-3 py-2 text-sm text-[#0B0D10]">
              <Landmark className="h-4 w-4 text-[#E1062C]" />
              Federal Register
            </span>
            <span className="flex items-center gap-2 rounded-lg bg-[#F6F5F2] px-3 py-2 text-sm text-[#0B0D10]">
              <FileText className="h-4 w-4 text-[#E1062C]" />
              Congress.gov
            </span>
            <span className="flex items-center gap-2 rounded-lg bg-[#F6F5F2] px-3 py-2 text-sm text-[#0B0D10]">
              <Database className="h-4 w-4 text-[#E1062C]" />
              FRED
            </span>
          </div>
        </div>

        {/* Recent Searches */}
        <div className="mt-auto">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
            Popular
          </p>
          <div className="flex flex-wrap gap-2">
            {popularSearches.map((topic) => (
              <button
                key={topic}
                type="button"
                onClick={() => submitHeroSearch(topic)}
                className="rounded-full border border-[rgba(11,13,16,0.08)] bg-white px-3 py-1.5 text-sm text-[#6B6F78] transition-colors hover:border-[#E1062C]/40 hover:text-[#E1062C] focus:outline-none focus:ring-2 focus:ring-[#E1062C]/30"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
